import { ComponentProps } from 'react'
import { View, Text, StyleSheet, ViewStyle } from 'react-native'
import ChipIconSvg from '@/assets/chips/chip-icon.svg'
import ChipPinkSvg from '@/assets/chips/chip-pink.svg'
import ChipOrangeSvg from '@/assets/chips/chip-orange.svg'
import ChipBlueSvg from '@/assets/chips/chip-blue.svg'
import ChipYellowSvg from '@/assets/chips/chip-yellow.svg'

const ChipIcon = (props: ComponentProps<typeof ChipPinkSvg>) => {
  return <ChipIconSvg {...props} />
}
const ChipPink = (props: ComponentProps<typeof ChipPinkSvg>) => {
  return <ChipPinkSvg {...props} />
}
const ChipOrange = (props: ComponentProps<typeof ChipOrangeSvg>) => {
  return <ChipOrangeSvg {...props} />
}
const ChipBlue = (props: ComponentProps<typeof ChipBlueSvg>) => {
  return <ChipBlueSvg {...props} />
}
const ChipYellow = (props: ComponentProps<typeof ChipYellowSvg>) => {
  return <ChipYellowSvg {...props} />
}

export const Chips = {
  Icon: ChipIcon,
  Pink: ChipPink,
  Orange: ChipOrange,
  Blue: ChipBlue,
  Yellow: ChipYellow
}

/**
 * Formats a numeric chip value into a human-readable string with abbreviated suffixes.
 *
 * Applies the following formatting rules:
 * - Values >= 1 billion: Displays with 'B' suffix (e.g., 1000000000 → "1B", 1500000000 → "1.5B")
 * - Values >= 1 million: Displays with 'M' suffix (e.g., 1000000 → "1M", 2500000 → "2.5M")
 * - Values >= 1 thousand: Displays with 'K' suffix (e.g., 1000 → "1K", 5000 → "5K")
 * - Values < 1 thousand: Displays as-is (e.g., 50 → "50", 999 → "999")
 *
 * Numbers are rounded to one decimal place, with trailing ".0" removed for cleaner display.
 *
 * @param value - The numeric chip value to format (should be non-negative)
 * @returns A formatted string representation of the chip value
 *
 * @example
 * ```ts
 * formatChipValue(100)       // "100"
 * formatChipValue(1500)      // "1.5K"
 * formatChipValue(5000)      // "5K"
 * formatChipValue(1000000)   // "1M"
 * formatChipValue(2500000)   // "2.5M"
 * formatChipValue(1000000000) // "1B"
 * ```
 */
export function formatChipValue(value: number): string {
  if (value >= 1e9) {
    const formatted = (value / 1e9).toFixed(1).replace(/\.0$/, '')
    return `${formatted}B`
  }

  if (value >= 1e6) {
    const formatted = (value / 1e6).toFixed(1).replace(/\.0$/, '')
    return `${formatted}M`
  }

  if (value >= 1e3) {
    const formatted = (value / 1e3).toFixed(1).replace(/\.0$/, '')
    return `${formatted}K`
  }

  return value.toString()
}

/**
 * Calculates chip values based on blind amounts
 * Index 0 = Small Blind (SB)
 * Index 1 = Big Blind (BB)
 * Index 2 = Blue chip (BB * 10)
 * Index 3 = Yellow chip (BB * 50)
 *
 * Example: SB=50, BB=100 → [50, 100, 1000, 5000]
 */
const calculateChipValues = (bigBlind: number) => {
  const smallBlind = bigBlind / 2
  return [
    smallBlind, // Pink chip (SB)
    bigBlind, // Orange chip (BB)
    bigBlind * 10, // Blue chip (10x BB)
    bigBlind * 50 // Yellow chip (50x BB)
  ]
}

export const getChipsDataWithValue = (bigBlind: number) => {
  const chipValues = calculateChipValues(bigBlind)
  // chipValues = [50, 100, 1000, 5000] if blind = 100

  // Define chip data with their corresponding components and colors
  const chipData = [
    { Component: ChipPink, value: chipValues[0], color: 'pink' },
    { Component: ChipOrange, value: chipValues[1], color: 'orange' },
    { Component: ChipBlue, value: chipValues[2], color: 'blue' },
    { Component: ChipYellow, value: chipValues[3], color: 'yellow' }
  ]
  return chipData
}

type ChipsStacksProps = {
  amount: number
  blind: number
  chipProps?: Pick<ComponentProps<typeof ChipYellowSvg>, 'width' | 'height'>
  chipContainerStyle?: ViewStyle
  containerStyle?: ViewStyle
  options?: {
    order?: 'desc' | 'asc'
    sort?: 'value' | 'count'
  }
}

export const ChipsStacks = (props: ChipsStacksProps) => {
  const chipData = getChipsDataWithValue(props.blind)

  // Calculate how many of each chip to display
  // Start from highest value and work down
  let remainingAmount = props.amount
  const chipCounts: number[] = []

  // Process chips in reverse order (highest value first)
  for (let i = chipData.length - 1; i >= 0; i--) {
    const count = Math.floor(remainingAmount / chipData[i].value)
    chipCounts[i] = count
    remainingAmount -= count * chipData[i].value
  }

  // Create chip stacks (only include chips with count > 0)
  const chipStacks = chipData
    .map((chip, chipIndex) => ({
      Component: chip.Component,
      value: chip.value,
      color: chip.color,
      count: chipCounts[chipIndex]
    }))
    .filter((stack) => stack.count > 0)

  const sortedStacks = props?.options?.sort === 'count' ? chipStacks.sort((a, b) => a.count - b.count) : chipStacks

  // Apply ordering based on options (default: 'desc' - highest value first)
  const orderedStacks = props.options?.order === 'asc' ? sortedStacks : sortedStacks.reverse()

  // Example: amount = 6150, blind = 100
  // Yellow (5000): 1 chip → 1150 remaining
  // Blue (1000): 1 chip → 150 remaining
  // Orange (100): 1 chip → 50 remaining
  // Pink (50): 1 chip → 0 remaining
  // Result: [Yellow Stack(1), Blue Stack(1), Orange Stack(1), Pink Stack(1)]

  return (
    <View style={[styles.container, props?.containerStyle]}>
      {orderedStacks.map((stack) => (
        <View key={stack.color} style={styles.stackContainer}>
          {/* Chip stack - render multiple chips with offset for stack effect */}
          <View style={styles.chipStackWrapper}>
            {Array(Math.min(stack.count, 5))
              .fill(null)
              .map((_, i) => (
                <View
                  key={`${stack.color}-chip-${i}`}
                  style={[
                    styles.chipLayer,
                    {
                      top: -i * 2,
                      left: 0
                    }
                  ]}
                >
                  <View style={props?.chipContainerStyle}>
                    <stack.Component {...props?.chipProps} />
                  </View>
                </View>
              ))}
            {/* Placeholder for height */}
            <View style={[styles.chipPlaceholder, props?.chipContainerStyle]}>
              <stack.Component {...props?.chipProps} />
            </View>
          </View>
        </View>
      ))}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    gap: 2
  },
  stackContainer: {
    position: 'relative',
    flexDirection: 'column',
    alignItems: 'center'
  },
  chipStackWrapper: {
    position: 'relative'
  },
  chipLayer: {
    position: 'absolute'
  },
  chipPlaceholder: {
    opacity: 0
  }
})
